import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useEffect, type ReactElement, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/**
 * Shared confirmation dialog (PLAN §3.5).
 *
 * - Variants `destructive` (red confirm) / `warning` / `info` colour the
 *   confirm button.
 * - `loading` disables both buttons + shows a spinner on confirm; the
 *   parent owns the async lifecycle (set `loading` true on click, false
 *   on settle). Prevents the double-trigger described in §4.13.
 * - Optional **type-to-confirm**: pass `confirmPhrase` to require the
 *   admin to type a phrase (e.g. order number) before confirm enables —
 *   validated via RHF + Zod for destructive ops on high-value entities.
 */
type ConfirmVariant = 'destructive' | 'warning' | 'info';

const VARIANT_COLOR: Record<ConfirmVariant, 'error' | 'warning' | 'primary'> = {
  destructive: 'error',
  warning: 'warning',
  info: 'primary',
};

export interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  /** Body copy or rich content. */
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  loading?: boolean;
  /** When set, the admin must type this exact phrase to enable confirm. */
  confirmPhrase?: string;
  /** Label for the type-to-confirm field. */
  confirmPhraseLabel?: string;
  /** Prefix for e2e ids: the confirm renders `${testId}-confirm`. */
  testId?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

type ConfirmForm = { phrase: string };

/**
 * Schema output shape is always `{ phrase: string }` (the field defaults
 * to `''`) so the RHF resolver typing stays stable whether or not a
 * phrase is required. When required, a `.refine` enforces the exact
 * match; otherwise any string (incl. empty) passes.
 */
const buildSchema = (phrase: string | undefined) =>
  z.object({
    phrase: z
      .string()
      .refine((v) => !phrase || v === phrase, { message: 'confirm.phraseMismatch' }),
  });

export const ConfirmationDialog = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'info',
  loading = false,
  confirmPhrase,
  confirmPhraseLabel,
  testId,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps): ReactElement => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ConfirmForm>({
    resolver: zodResolver(buildSchema(confirmPhrase)),
    mode: 'onChange',
    defaultValues: { phrase: '' },
  });

  // Clear the type-to-confirm field whenever the dialog re-opens.
  useEffect(() => {
    if (open) reset({ phrase: '' });
  }, [open, reset]);

  const requiresPhrase = Boolean(confirmPhrase);
  const confirmDisabled = loading || (requiresPhrase && !isValid);

  const submit = (): void => {
    if (confirmDisabled) return;
    onConfirm();
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{description}</DialogContentText>
        {requiresPhrase ? (
          <form onSubmit={handleSubmit(submit)}>
            <TextField
              {...register('phrase')}
              fullWidth
              size="small"
              // Autofocus is intentional: a type-to-confirm dialog the admin
              // opened deliberately; focusing the field is the expected flow.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={loading}
              label={confirmPhraseLabel ?? t('confirm.phraseLabel')}
              placeholder={confirmPhrase}
              error={Boolean(errors.phrase)}
              helperText={errors.phrase ? t('confirm.phraseMismatch') : ' '}
              sx={{ mt: 2 }}
            />
          </form>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {cancelLabel ?? t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color={VARIANT_COLOR[variant]}
          onClick={submit}
          disabled={confirmDisabled}
          loading={loading}
          data-testid={testId ? `${testId}-confirm` : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
